import { Phone, Mail, ArrowUpRight, Instagram, MapPin, MessageCircle, Link2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useShop } from "../context/ShopContext";
import { scrollToSection } from "../lib/scroll";
import { CONTACT_PHONE, CONTACT_EMAIL } from "../i18n";

const TikTokIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.6 5.8a4.8 4.8 0 0 1-3.6-4.6h-3.5v13.6a3.1 3.1 0 1 1-3.1-3.1c.3 0 .6 0 .9.1V8.2a6.7 6.7 0 1 0 5.7 6.6V8.6a8.3 8.3 0 0 0 4.6 1.4V6.5c-.3 0-.7-.1-1-.2z" />
  </svg>
);

const hrefFor = (c) => {
  switch (c.type) {
    case "phone":
      return `tel:${c.value.replace(/\s/g, "")}`;
    case "email":
      return `mailto:${c.value}`;
    case "whatsapp":
      return `https://wa.me/${c.value.replace(/[^0-9]/g, "")}`;
    case "instagram":
      return `https://instagram.com/${c.value.replace(/^@/, "")}`;
    case "tiktok":
      return `https://tiktok.com/@${c.value.replace(/^@/, "")}`;
    case "link":
      return c.value;
    default:
      return null;
  }
};

const IconFor = ({ type, size = 15 }) => {
  if (type === "phone") return <Phone size={size} />;
  if (type === "email") return <Mail size={size} />;
  if (type === "whatsapp") return <MessageCircle size={size} />;
  if (type === "address") return <MapPin size={size} />;
  if (type === "instagram") return <Instagram size={size} />;
  if (type === "tiktok") return <TikTokIcon />;
  return <Link2 size={size} />;
};

const Footer = () => {
  const { t, lang, contacts } = useShop();
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { label: t.nav.catalog, href: "/urunler" },
    { label: t.nav.values, href: "#values" },
    { label: t.nav.b2b, href: "#b2b" },
    { label: t.nav.story, href: "#story" },
  ];

  const go = (href) => {
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToSection(href), 700);
      } else {
        scrollToSection(href);
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  const list =
    contacts === null
      ? [
          { id: "def-phone", type: "phone", label_tr: "Telefon", label_en: "Phone", value: CONTACT_PHONE },
          { id: "def-email", type: "email", label_tr: "E-posta", label_en: "Email", value: CONTACT_EMAIL },
          { id: "def-addr", type: "address", label_tr: "Adres", label_en: "Address", value: t.footer.address },
          { id: "def-ig", type: "instagram", label_tr: "Instagram", label_en: "Instagram", value: "volparyapi" },
          { id: "def-tt", type: "tiktok", label_tr: "TikTok", label_en: "TikTok", value: "vls.dolap" },
        ]
      : contacts;

  const contactRows = list.filter((c) => ["phone", "email", "whatsapp", "address"].includes(c.type));
  const socials = list.filter((c) => ["instagram", "tiktok", "link"].includes(c.type));

  return (
    <footer className="bg-brown text-cream overflow-hidden" data-testid="footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-20 md:pt-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pb-16">
          <div>
            <p className="font-serif text-xl italic text-cream/80 max-w-xs leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-olive-pale mb-6">
              {t.footer.nav}
            </p>
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-sm text-cream/70 hover:text-cream w-fit transition-colors duration-300"
                  data-testid={`footer-link-${l.href.replace(/[#/]/g, "")}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-olive-pale mb-6">
              {t.footer.contact}
            </p>
            <div className="flex flex-col gap-4" data-testid="footer-contact-list">
              {contactRows.map((c) => {
                const href = hrefFor(c);
                const label = lang === "tr" ? c.label_tr : c.label_en;
                const inner = (
                  <>
                    <span className="shrink-0 mt-0.5">
                      <IconFor type={c.type} />
                    </span>
                    <span>
                      <span className="block text-[10px] tracking-[0.25em] uppercase text-cream/40 mb-0.5">
                        {label}
                      </span>
                      {c.value}
                    </span>
                  </>
                );
                return href ? (
                  <a
                    key={c.id}
                    href={href}
                    target={["whatsapp", "link"].includes(c.type) ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-sm text-cream/70 hover:text-cream transition-colors duration-300"
                    data-testid={`footer-contact-${c.id}`}
                  >
                    {inner}
                  </a>
                ) : (
                  <p
                    key={c.id}
                    className="flex items-start gap-3 text-sm text-cream/70 leading-relaxed"
                    data-testid={`footer-contact-${c.id}`}
                  >
                    {inner}
                  </p>
                );
              })}
            </div>
            {socials.length > 0 && (
              <>
                <p className="mt-8 text-[11px] tracking-[0.3em] uppercase text-olive-pale">
                  {t.footer.social}
                </p>
                <div className="mt-4 flex items-center gap-3" data-testid="footer-social-list">
                  {socials.map((c) => (
                    <a
                      key={c.id}
                      href={hrefFor(c)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-cream/60 hover:text-cream hover:border-cream/50 transition-colors duration-300"
                      aria-label={lang === "tr" ? c.label_tr : c.label_en}
                      data-testid={`footer-social-${c.id}`}
                    >
                      <IconFor type={c.type} size={16} />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-cream/10 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/40">
          <span>© {new Date().getFullYear()} VLP Dolap — VOLPAR. {t.footer.rights}</span>
          <span className="tracking-[0.25em] uppercase">{t.footer.location}</span>
        </div>
      </div>

      <div className="relative select-none pointer-events-none" aria-hidden="true">
        <p className="font-serif font-medium text-center leading-[0.8] text-cream/[0.08] text-[22vw] md:text-[18vw] tracking-tight -mb-[4vw]">
          VLP DOLAP
        </p>
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 pointer-events-auto w-12 h-12 rounded-full border border-cream/20 flex items-center justify-center text-cream/60 hover:text-cream hover:border-cream/50 transition-colors duration-300"
          aria-label="Back to top"
          data-testid="back-to-top-button"
        >
          <ArrowUpRight size={18} />
        </a>
      </div>
    </footer>
  );
};

export default Footer;
